import type { PreventionAction } from "./prevention-action.types"
import type { Population } from "@/entities/population/model/population.types"

/** Perimetre d'une mesure RPS : ses fonctions, sinon tout l'etablissement */
export function getRpsScopeLabel(action: Pick<PreventionAction, "populationIds">, populations: Population[]) {
  const names = populations
    .filter((population) => action.populationIds.includes(population.id))
    .map((population) => population.name)
  return names.length ? names.join(", ") : "Tout l'établissement"
}

export function isPreventionActionWithoutDate(action: PreventionAction) {
  if (action.cStatus === "done") return false

  if (action.cStatus === "pending") {
    return !action.dateStarted && !action.datePlanned
  }

  return !action.dateStartedPlanned && !action.datePlanned
}

/** Display category only: the underlying todo/pending status stays unchanged. */
export function isPreventionActionToPlan(action: PreventionAction) {
  return !action.dateDone && action.cStatus !== "done" && !action.datePlanned
}

/** A planned measure becomes overdue the day after its due date (local calendar day). */
export function isPreventionActionOverdue(action: PreventionAction, now = new Date()) {
  if (action.dateDone || action.cStatus === "done" || !action.datePlanned) return false
  const due = new Date(action.datePlanned)
  const today = new Date(now)
  due.setHours(0, 0, 0, 0)
  today.setHours(0, 0, 0, 0)
  return due.getTime() < today.getTime()
}

/** Mesures groupees par risque : les vues et les taches du panneau lisent le meme regroupement. */
export function groupMeasuresByRisk(actions: PreventionAction[]) {
  const byRisk = new Map<string, PreventionAction[]>()
  for (const action of actions) {
    if (!action.PhysicalRiskId) continue
    const group = byRisk.get(action.PhysicalRiskId) ?? []
    group.push(action)
    byRisk.set(action.PhysicalRiskId, group)
  }
  return byRisk
}

export type PreventionMeasuresView = "in-place" | "planned" | "to-plan" | "overdue" | "missing"

export const PREVENTION_MEASURES_VIEWS: Record<PreventionMeasuresView, { title: string; subtitle: string }> = {
  "in-place": { title: "Mesures réalisées", subtitle: "Consultez les mesures de prévention déjà réalisées." },
  planned: { title: "Mesures prévues", subtitle: "Consultez les mesures de prévention avec une échéance à venir." },
  "to-plan": {
    title: "Mesures à planifier",
    subtitle: "Définissez une échéance pour les mesures de prévention non planifiées."
  },
  overdue: { title: "Mesures en retard", subtitle: "Traitez les mesures de prévention dont l’échéance est dépassée." },
  missing: { title: "Mesures absentes", subtitle: "Identifiez les risques sans mesure de prévention." }
}

export function filterMeasuresForView(actions: PreventionAction[], view: PreventionMeasuresView, now = new Date()) {
  if (view === "missing") return []
  return actions.filter((action) => {
    const isDone = Boolean(action.dateDone) || action.cStatus === "done"
    if (view === "in-place") return isDone
    if (isDone) return false
    if (view === "to-plan") return isPreventionActionToPlan(action)
    if (isPreventionActionToPlan(action)) return false
    const isOverdue = isPreventionActionOverdue(action, now)
    return view === "overdue" ? isOverdue : !isOverdue
  })
}

export function matchesMeasuresView(actions: PreventionAction[], view: PreventionMeasuresView, now = new Date()) {
  return view === "missing" ? actions.length === 0 : filterMeasuresForView(actions, view, now).length > 0
}
